import { useRef, useState } from 'react'
import { Alert, Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material'
import {
  Add as AddIcon,
  AudioFile as AudioFileIcon,
  Delete as DeleteIcon,
  LibraryBooks as LibraryBooksIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from './primitives'
import { ChoiceEditor, TrueFalseEditor } from './ChoiceEditors'
import { defaultContentFor } from '../contentTypes'
import type { ChoiceContent, CrosswordContent, ListeningContent, TrueFalseContent } from '../contentTypes'
import type { ThemeWord } from '../themeWords'
import { exercisesApi } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'

/**
 * Редакторы медийных типов: кроссворд/филворд и аудирование с вложенными
 * вопросами.
 */

type EditorProps<T> = {
  content: T
  onChange: (content: T) => void
  disabled?: boolean
  themeWords?: ThemeWord[]
}

export function CrosswordEditor({ content, onChange, disabled, themeWords = [] }: EditorProps<CrosswordContent>) {
  const { t } = useTranslation()

  const fillFromTheme = () => {
    const words = themeWords
      .filter((word) => /^[\p{L}]{2,}$/u.test(word.text.trim()))
      .slice(0, 12)
      .map((word) => ({ answer: word.text.trim().toUpperCase(), clue: word.translation ?? '' }))

    if (words.length >= 2) onChange({ ...content, words })
  }

  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label={t('exercises.editor.crosswordVariant')}
        value={content.variant}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, variant: event.target.value as CrosswordContent['variant'] })}
      >
        <MenuItem value="crossword">{t('exercises.editor.crossword')}</MenuItem>
        <MenuItem value="wordsearch">{t('exercises.editor.wordsearch')}</MenuItem>
      </TextField>

      <Alert severity="info">{t('exercises.editor.crosswordGridHint')}</Alert>

      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <SectionLabel text={t('exercises.editor.crosswordWords')} />
          {themeWords.length > 0 && (
            <Button size="small" startIcon={<LibraryBooksIcon />} disabled={disabled} onClick={fillFromTheme}>
              {t('exercises.editor.fromThemeWords')}
            </Button>
          )}
        </Stack>

        <Stack spacing={1}>
          {content.words.map((word, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                label={t('exercises.editor.crosswordAnswer')}
                value={word.answer ?? ''}
                disabled={disabled}
                onChange={(event) => {
                  const words = [...content.words]
                  words[index] = { ...word, answer: event.target.value.toUpperCase() }
                  onChange({ ...content, words })
                }}
              />
              <TextField
                fullWidth
                size="small"
                label={t('exercises.editor.crosswordClue')}
                value={word.clue ?? ''}
                disabled={disabled}
                onChange={(event) => {
                  const words = [...content.words]
                  words[index] = { ...word, clue: event.target.value }
                  onChange({ ...content, words })
                }}
              />
              <IconButton
                size="small"
                disabled={disabled || content.words.length <= 2}
                onClick={() => onChange({ ...content, words: content.words.filter((_, i) => i !== index) })}
                aria-label={t('common.delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={() => onChange({ ...content, words: [...content.words, { answer: '', clue: '' }] })}
            >
              {t('exercises.editor.addWord')}
            </Button>
          </Box>
        </Stack>
      </Box>

      {content.grid && (
        <Typography variant="caption" color="text.secondary">
          {/* У филворда укладок в контенте нет — считаем через ?? [], иначе
              подпись падала бы на сетке без placements */}
          {t('exercises.editor.crosswordPlaced', { count: (content.grid.placements ?? []).length })}
        </Typography>
      )}
    </Stack>
  )
}

/**
 * Загрузка аудиофайла задания: место расходуется из хранилища учителя.
 *
 * Отдельная кнопка, а не поле «путь к файлу»: путь учителю взять негде, и
 * форма аудирования была недоводимой — задание сохранялось без звука.
 */
function AudioUploadButton({
  onUploaded,
  disabled,
  label,
}: {
  onUploaded: (path: string, url: string) => void
  disabled?: boolean
  label: string
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const result = await exercisesApi.uploadAudio(file)
      onUploaded(result.path, result.url)
    } catch (err) {
      // Сообщение сервера показываем как есть: 422 про нехватку места учитель
      // должен прочитать целиком, иначе он повторяет загрузку по кругу
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/webm,.mp3,.m4a,.ogg,.wav"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void upload(file)
          // Сброс значения: иначе повторный выбор того же файла не даёт события
          event.target.value = ''
        }}
      />
      <Button
        size="small"
        startIcon={<AudioFileIcon />}
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? t('exercises.editor.audioUploading', 'Загружаем…') : label}
      </Button>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

/** Вложенные вопросы аудирования — те же типы, что и обычные задания */
const LISTENING_QUESTION_TYPES = ['choice', 'true_false'] as const

export function ListeningEditor({ content, onChange, disabled }: EditorProps<ListeningContent>) {
  const { t } = useTranslation()
  const source = content.audio?.source ?? 'tts'

  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label={t('exercises.editor.audioSource')}
        value={source}
        disabled={disabled}
        onChange={(event) =>
          onChange({
            ...content,
            audio: { source: event.target.value as 'tts' | 'upload', text: '', path: null },
          })
        }
      >
        <MenuItem value="tts">{t('exercises.editor.audioTts')}</MenuItem>
        <MenuItem value="upload">{t('exercises.editor.audioUpload')}</MenuItem>
      </TextField>

      {source === 'tts' ? (
        <>
          {/* Честное предупреждение: озвучки заданий на сервере нет, и задание
              с одним текстом ученик откроет без звука */}
          <Alert severity="warning">
            {t(
              'exercises.editor.audioTtsUnavailable',
              'Автоматической озвучки заданий пока нет: ученик услышит тишину. Выберите «Загруженный файл» и приложите запись — текст ниже останется расшифровкой для вас.',
            )}
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label={t('exercises.editor.audioText')}
            helperText={t('exercises.editor.audioTextHint')}
            value={content.audio?.text ?? ''}
            disabled={disabled}
            onChange={(event) => onChange({ ...content, audio: { ...content.audio, source: 'tts', text: event.target.value } })}
          />
        </>
      ) : (
        <Stack spacing={1}>
          <AudioUploadButton
            disabled={disabled}
            label={
              content.audio?.path
                ? t('exercises.editor.audioReplace', 'Заменить файл')
                : t('exercises.editor.audioUploadFile', 'Загрузить аудиофайл')
            }
            onUploaded={(path, url) =>
              onChange({ ...content, audio: { ...content.audio, source: 'upload', path, url } })
            }
          />

          {content.audio?.path ? (
            <Stack spacing={1}>
              {/* Проигрывание прямо в форме: без него учитель не проверит, что
                  загрузился нужный файл, до выдачи задания ученику */}
              {content.audio.url ? (
                <Box component="audio" controls src={content.audio.url} sx={{ width: '100%', maxWidth: 420 }} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('exercises.editor.audioNoPreview', 'Файл загружен: {{path}}', { path: content.audio.path })}
                </Typography>
              )}
              <Box>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={disabled}
                  onClick={() =>
                    onChange({ ...content, audio: { ...content.audio, source: 'upload', path: null, url: null } })
                  }
                >
                  {t('exercises.editor.audioRemove', 'Убрать файл')}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('exercises.editor.audioUploadHint', 'Форматы mp3, m4a, ogg, wav. Место расходуется из вашего хранилища.')}
            </Typography>
          )}
        </Stack>
      )}

      <TextField
        size="small"
        type="number"
        sx={{ maxWidth: 220 }}
        label={t('exercises.editor.maxPlays')}
        inputProps={{ min: 1, max: 20 }}
        value={content.max_plays ?? ''}
        disabled={disabled}
        onChange={(event) =>
          onChange({ ...content, max_plays: event.target.value === '' ? null : Number(event.target.value) })
        }
      />

      <Box>
        <SectionLabel text={t('exercises.editor.listeningQuestions')} hint={t('exercises.editor.listeningQuestionsHint')} />
        <Stack spacing={2}>
          {content.questions.map((question, index) => (
            <Box key={index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" label={t(`exercises.types.${question.type}.name`)} />
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => onChange({ ...content, questions: content.questions.filter((_, i) => i !== index) })}
                  aria-label={t('common.delete')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>

              {question.type === 'choice' ? (
                <ChoiceEditor
                  content={question.content as unknown as ChoiceContent}
                  disabled={disabled}
                  onChange={(nested) => {
                    const questions = [...content.questions]
                    questions[index] = { ...question, content: nested as unknown as Record<string, unknown> }
                    onChange({ ...content, questions })
                  }}
                />
              ) : (
                <TrueFalseEditor
                  content={question.content as unknown as TrueFalseContent}
                  disabled={disabled}
                  onChange={(nested) => {
                    const questions = [...content.questions]
                    questions[index] = { ...question, content: nested as unknown as Record<string, unknown> }
                    onChange({ ...content, questions })
                  }}
                />
              )}
            </Box>
          ))}

          <Stack direction="row" spacing={1}>
            {LISTENING_QUESTION_TYPES.map((type) => (
              <Button
                key={type}
                size="small"
                startIcon={<AddIcon />}
                disabled={disabled}
                onClick={() =>
                  onChange({
                    ...content,
                    questions: [...content.questions, { type, content: defaultContentFor(type) }],
                  })
                }
              >
                {t(`exercises.types.${type}.name`)}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  )
}
